/**
 * Os exames do paciente na tela (#811, fase 3): a etapa atual, o texto do ok
 * e a lista de etapas sendo montada. Sem React, para o teste não precisar de
 * tela.
 *
 * A regra mora no servidor (o ok segue a ordem; só a última se desfaz). Aqui
 * ela só decide qual botão aparece: mostrar "Dar ok" numa etapa que o servidor
 * recusaria é uma porta que existe só para dizer não.
 */

import type {
  CriarExameDoPaciente,
  EtapaDaClinica,
  EtapaDoExame,
  EtapaNovaDoExame,
  ExameDoPaciente,
} from '@cliqcrm/contracts';

/** Até quantas etapas um pedido pode ter: o mesmo limite do servidor. */
export const LIMITE_DE_ETAPAS = 20;

/** A próxima etapa sem ok, na ordem do pedido: a única que recebe o ok. */
export function etapaAtual(exame: ExameDoPaciente): EtapaDoExame | null {
  return ordenadas(exame.etapas).find((etapa) => etapa.concluida_em === null) ?? null;
}

/** A última etapa com ok: a única que se desfaz. */
export function ultimaFeita(exame: ExameDoPaciente): EtapaDoExame | null {
  const feitas = ordenadas(exame.etapas).filter((etapa) => etapa.concluida_em !== null);
  return feitas[feitas.length - 1] ?? null;
}

function ordenadas(etapas: readonly EtapaDoExame[]): EtapaDoExame[] {
  return [...etapas].sort((a, b) => a.ordem - b.ordem);
}

export type SituacaoDoExame = 'cancelado' | 'concluido' | 'sem-etapas' | 'em-andamento';

export function situacaoDoExame(exame: ExameDoPaciente): SituacaoDoExame {
  if (exame.cancelado_em !== null) return 'cancelado';
  if (exame.etapas.length === 0) return 'sem-etapas';
  return etapaAtual(exame) === null ? 'concluido' : 'em-andamento';
}

/** "16/10/2026 09:12" no fuso de quem olha. */
export function instanteCurto(iso: string): string {
  const data = new Date(iso);
  return `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** "ok em 16/10/2026 09:12, por Ana", ou "aguardando". */
export function textoDoOk(etapa: EtapaDoExame): string {
  if (etapa.concluida_em === null) return 'aguardando';
  const quem = etapa.concluida_por?.nome ?? 'alguém que não está mais na equipe';
  return `ok em ${instanteCurto(etapa.concluida_em)}, por ${quem}`;
}

/** As etapas da clínica que um pedido novo sugere: as ativas, na ordem dela. */
export function etapasSugeridas(catalogo: readonly EtapaDaClinica[]): EtapaNovaDoExame[] {
  return [...catalogo]
    .filter((etapa) => etapa.ativa)
    .sort((a, b) => a.ordem - b.ordem)
    .map((etapa) => ({ nome: etapa.nome, etapa_da_clinica_id: etapa.id }));
}

/** Troca a etapa de lugar com a vizinha; fora da lista, devolve a mesma. */
export function mover<T>(lista: readonly T[], indice: number, direcao: -1 | 1): T[] {
  const destino = indice + direcao;
  if (destino < 0 || destino >= lista.length) return [...lista];
  const nova = [...lista];
  [nova[indice], nova[destino]] = [nova[destino]!, nova[indice]!];
  return nova;
}

/** As etapas abertas de um pedido, no formato de montar a lista de novo. */
export function etapasAbertas(exame: ExameDoPaciente): EtapaNovaDoExame[] {
  return ordenadas(exame.etapas)
    .filter((etapa) => etapa.concluida_em === null)
    .map((etapa) => ({ nome: etapa.nome, etapa_da_clinica_id: etapa.etapa_da_clinica_id }));
}

/** Os campos do formulário, como texto: o vazio vira `null` no corpo. */
export interface FormularioDoExame {
  readonly exame: string;
  readonly codigoDoExame: string;
  readonly codigoDoPedido: string;
  readonly laboratorio: string;
  readonly solicitante: string;
  readonly urgente: boolean;
  readonly observacao: string;
}

export const FORMULARIO_VAZIO: FormularioDoExame = {
  exame: '',
  codigoDoExame: '',
  codigoDoPedido: '',
  laboratorio: '',
  solicitante: '',
  urgente: false,
  observacao: '',
};

export function formularioDe(exame: ExameDoPaciente): FormularioDoExame {
  return {
    exame: exame.exame,
    codigoDoExame: exame.codigo_do_exame ?? '',
    codigoDoPedido: exame.codigo_do_pedido ?? '',
    laboratorio: exame.laboratorio ?? '',
    solicitante: exame.solicitante ?? '',
    urgente: exame.urgente,
    observacao: exame.observacao ?? '',
  };
}

const ouNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());

export function camposDoCorpo(formulario: FormularioDoExame): Omit<CriarExameDoPaciente, 'etapas'> {
  return {
    exame: formulario.exame.trim(),
    codigo_do_exame: ouNulo(formulario.codigoDoExame),
    codigo_do_pedido: ouNulo(formulario.codigoDoPedido),
    laboratorio: ouNulo(formulario.laboratorio),
    solicitante: ouNulo(formulario.solicitante),
    urgente: formulario.urgente,
    observacao: ouNulo(formulario.observacao),
  };
}

/**
 * Desde quando a etapa atual espera o ok: o ok da etapa anterior, ou o pedido
 * criado, se nenhuma tem ok. `null` quando não há etapa atual.
 */
export function esperandoDesde(exame: ExameDoPaciente): string | null {
  if (exame.cancelado_em !== null || etapaAtual(exame) === null) return null;
  return ultimaFeita(exame)?.concluida_em ?? exame.criado_em;
}

/** Dias inteiros entre o instante e agora: `0` no mesmo dia de 24 horas. */
export function diasDesde(iso: string, agora: Date = new Date()): number {
  return Math.max(0, Math.floor((agora.getTime() - new Date(iso).getTime()) / 86_400_000));
}

/**
 * A partir de quantos dias numa etapa o pedido conta como parado.
 *
 * Provisório até os lembretes da clínica (o plano prevê "exame parado depois
 * de N dias" nas configurações): por ora a tela só marca, não cobra.
 */
export const DIAS_PARA_PARADO = 7;

/** "hoje", "há 1 dia", "há 12 dias". */
export function haDias(dias: number): string {
  if (dias === 0) return 'hoje';
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

/** "16/10 09:12": o carimbo curto do ok, sem o ano. */
export function carimbo(iso: string): string {
  const data = new Date(iso);
  return `${data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}
